import React from 'react';
import { get, find } from 'lodash';

import { STOREFRONT_LIMIT_COUNT } from 'constants/storefront';
import { normalizeUrl } from 'utils';

import Tooltip from 'components/tooltip';

const { APP_DEPLOYMENT_PUBLIC_URL, CLIENT_URL } = process.env;

const THEME_LINKS = {
  origin: 'https://github.com/swellstores/origin-theme/',
  horizon: 'https://github.com/swellstores/horizon',
};

export function getStorefrontURL(storefront, preview = false, editor = false) {
  if (!storefront) return '';

  const {
    id,
    slug,
    primary,
    domains,
    external,
    hosted,
    app_id,
    client_id,
    environment_id,
  } = storefront;

  // External storefronts
  if (!app_id && external?.custom && external?.custom_url) {
    return external.custom_url;
  }

  // Primary with domain
  if (primary && !preview && !editor) {
    const primaryDomain = find(domains?.results, {
      primary: true,
      verified: true,
    });
    if (primaryDomain) {
      return normalizeUrl(primaryDomain.name, {
        defaultProtocol: 'https://',
      });
    }
  }

  // V1 hosted storefronts
  if (hosted) {
    return CLIENT_URL.replace('CLIENT_ID', slug);
  }

  if (editor) {
    return CLIENT_URL.replace('CLIENT_ID', `${client_id}--${id}--dev`);
  } else if (preview) {
    return CLIENT_URL.replace('CLIENT_ID', `${client_id}--${id}--preview`);
  } else if (!primary || environment_id) {
    return CLIENT_URL.replace('CLIENT_ID', `${client_id}--${id}`);
  }

  return CLIENT_URL.replace('CLIENT_ID', client_id);
}

export function getLocalProxyStorefrontURL(session, editor = false) {
  const { id, client_id, local_proxy } = session;

  if (local_proxy) {
    if (editor) {
      return CLIENT_URL.replace('CLIENT_ID', `${client_id}--${id}--local--dev`);
    } else {
      return CLIENT_URL.replace('CLIENT_ID', `${client_id}--${id}--local`);
    }
  }

  return '';
}

export function storefrontPublicDeploymentURL(storefront) {
  const { preview_deployment_name } = storefront;
  return APP_DEPLOYMENT_PUBLIC_URL.replace(
    'DEPLOYMENT_ID',
    preview_deployment_name,
  );
}

export function storefrontCheckoutURL(clientId, dev = false) {
  return CLIENT_URL.replace('CLIENT_ID', `${clientId}${dev ? '--dev' : ''}`);
}

export function renderStorefrontV1ThemeDesc(storefront) {
  return (
    <>
      <a
        href={THEME_LINKS[storefront.source_id]}
        target="_blank"
        rel="noreferrer"
      >
        {storefront.source?.name || storefront.source_id} theme
      </a>{' '}
      v{storefront.source_version}
    </>
  );
}

/**
 * Returns the storefront link for a given collection and record.
 * @param {string} collection
 * @param {object} record
 * @param {object} storefront
 * @returns {string}
 */
export function storefrontCollectionLink(collection, record, storefront) {
  const storefrontUrl = getStorefrontURL(storefront);

  let values = record;

  // Default pattern
  let pattern = `/${collection}/:slug`;

  // Find the page config if defined by a storefront app
  const storefrontThemeConfig = storefront?.app?.storefront?.theme;
  if (storefrontThemeConfig) {
    const storefrontAppCollectionTemplate =
      storefrontThemeConfig?.template_collections?.[collection];
    const storefrontThemePageConfig = find(storefrontThemeConfig?.pages, {
      id: storefrontAppCollectionTemplate,
    });

    if (storefrontThemePageConfig?.url) {
      pattern = storefrontThemePageConfig.url;
    }
  }

  try {
    const url = pattern
      .replace(/:([\w.]+)/g, (match) => {
        const key = match.slice(1);
        const value = get(values, key);
        if (value) {
          return value;
        }
        return '';
      })
      .replace(/\/\//g, '/');

    return `${storefrontUrl}${url}`;
  } catch {
    return `${storefrontUrl}/${collection}/${values?.slug || ''}`;
  }
}

/**
 * Returns the page ID from a given route.
 * @param {string} route
 * @param {Array} pages
 * @returns {string|undefined}
 */
export function getPageIdFromRoute(href, pages) {
  const route = href.split('?')[0];
  return find(pages, (p) => {
    if (p.url.match(':slug')) {
      return route.includes(p.url.split('/')[1]);
    }

    if (p.url.match(':id')) {
      const parts = p.url.split('/');
      return route.includes(parts[1]) && route.includes(parts[2]);
    }

    return p.url === route;
  })?.id;
}

/**
 * Checks if the storefront limit has been reached.
 * Returns the limit if reached, false otherwise.
 * @param {object} storefront
 * @returns {number|boolean}
 */
export function storefrontLimitReached(storefronts) {
  return storefronts?.count >= STOREFRONT_LIMIT_COUNT
    ? STOREFRONT_LIMIT_COUNT
    : false;
}

/**
 * Render element with tooltip indicating storefront limit.
 * @param {object} storefronts
 * @param {function} render
 * @param {object} tooltipProps
 * @returns {React.ReactNode}
 */
export function renderWithStorefrontLimitTooltip(
  storefronts,
  render,
  tooltipProps,
) {
  if (storefrontLimitReached(storefronts)) {
    return (
      <Tooltip
        message={`You've reached the limit of ${STOREFRONT_LIMIT_COUNT} storefronts.`}
        {...tooltipProps}
      >
        {render(true)}
      </Tooltip>
    );
  }

  return render(false);
}

/**
 * Returns the options for the storefront theme template select field.
 * @param {string[]} templates
 * @returns {object[]}
 */
export function storefrontThemeTemplateSelectOptions(templates = []) {
  const options = [
    {
      value: '',
      label: 'Default template',
    },
  ];

  for (const templateId of templates) {
    options.push({
      value: templateId,
      label: templateId,
    });
  }

  return options;
}

/**
 * Renders the theme template selector field for a given collection.
 * @param {string} collection
 * @param {object} themeTemplates
 * @param {function} handler
 * @returns {React.ReactNode|null}
 */
export function renderThemeTemplateSelector(
  collection,
  themeTemplates,
  handler,
) {
  if (!themeTemplates?.[collection]) {
    return null;
  }

  const options = storefrontThemeTemplateSelectOptions(
    themeTemplates[collection].list,
  );

  return handler({
    label: 'Theme template',
    help: 'Template to display this product in your storefront theme',
    options,
  });
}
